import { Link } from 'react-router-dom';

const INK = '#0f172a';
const MUTED = '#64748b';
const INDIGO = '#6366f1';
const GREEN = '#15803d';

// Date this codebase's network behavior was last reviewed. Update if re-reviewed.
const REVIEW_DATE = 'September 5, 2026';

function Check({ children }) {
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: '#334155', lineHeight: 1.6, marginBottom: 10 }}>
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span>{children}</span>
    </li>
  );
}

function CodeValidation() {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: INK, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1000, margin: '0 auto', padding: '18px 32px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontWeight: 800, fontSize: 16, color: INK }}>
            <span style={{ display: 'inline-flex', width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`, alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            LE Tech Tools
          </Link>
          <Link to="/tools" style={{ color: INDIGO, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>All tools →</Link>
        </nav>
      </div>

      <main style={{ maxWidth: 800, margin: '0 auto', padding: '40px 32px 80px' }}>
        {/* Hero */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
          background: '#f0fdf4', color: GREEN, padding: '6px 14px', borderRadius: 20, border: '1px solid #bbf7d0', marginBottom: 20,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" />
          </svg>
          Privacy verified
        </div>

        <h1 style={{ fontSize: 'clamp(30px, 5vw, 44px)', fontWeight: 800, letterSpacing: '-0.7px', margin: '0 0 16px', lineHeight: 1.1 }}>
          Your data never leaves your browser
        </h1>
        <p style={{ fontSize: 18, color: MUTED, lineHeight: 1.65, margin: '0 0 12px' }}>
          Every tool on LE Tech Tools runs <strong style={{ color: INK }}>100% client-side</strong> — the parsing,
          decryption, and document generation all happen on your own device. <strong style={{ color: INK }}>No uploaded
          file, plate, image, document, or case data is ever sent to a server.</strong>
        </p>
        <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.65, margin: '0 0 36px' }}>
          You can prove it yourself: once this site loads, <strong style={{ color: INK }}>turn off your Wi-Fi</strong> —
          every tool keeps working (except the two collaborative, cloud-backed tools noted below, and the optional
          live-lookup half of the IP and Domain Intelligence tool). Nothing needs the network, because nothing is being sent.
        </p>

        {/* Independent review */}
        <section style={{
          background: '#fff', border: '1.5px solid #bbf7d0', borderRadius: 14, padding: '26px 28px', marginBottom: 36,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            Reviewed &amp; verified
          </h2>
          <p style={{ fontSize: 15.5, color: '#334155', lineHeight: 1.7, margin: '0 0 14px' }}>
            The application&rsquo;s code has been reviewed to trace <strong>every network request it can make</strong>,
            confirming that no uploaded file, plate, image, document, or case data is transmitted anywhere. The only outbound
            network activity is the short, fully-disclosed list below. <span style={{ color: MUTED }}>Last reviewed {REVIEW_DATE}.</span>
          </p>
          <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: 0 }}>
            The review confirmed that all file parsing, GPG/PGP decryption, ZIP handling, PDF reading and generation, and EXIF
            extraction run entirely in-browser; that there are no hidden analytics beacons carrying your inputs; and that there
            are no code-injection sinks. Best of all, you don&rsquo;t have to take our word for it — because the app is fully
            client-side, you can verify all of it yourself in your browser&rsquo;s developer tools. Nothing is hidden on a server.
          </p>
        </section>

        {/* What runs locally */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', margin: '0 0 16px' }}>What happens on your device</h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 36px' }}>
          <Check>Warrant &amp; subpoena return parsing (Facebook, Instagram, Snapchat, Google, Apple)</Check>
          <Check>Apple return decryption — GPG/PGP runs in your browser; the passphrase never leaves your device</Check>
          <Check>LPR/ALPR analysis and CDR analysis</Check>
          <Check>PDF reading, EXIF metadata extraction, and document/spreadsheet generation</Check>
          <Check>Preservation requests and subpoena riders — built locally and downloaded directly</Check>
        </ul>

        {/* The exceptions */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', margin: '0 0 8px' }}>The only network exceptions</h2>
        <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.6, margin: '0 0 20px' }}>
          In the interest of full transparency, here is <em>everything</em> the app ever sends over the network. None of it
          includes your files, uploads, or case data.
        </p>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>1. Video Canvass Tracker <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 20, padding: '2px 8px', marginLeft: 6 }}>By design</span></h3>
          <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>
            Because a team shares one live map in real time, it stores its canvass pins in the cloud (Firebase) and
            turns typed addresses into map coordinates using OpenStreetMap&rsquo;s public geocoder. Access is via a
            private, unguessable link, and it&rsquo;s clearly labeled as cloud-backed inside the app.
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>2. Area Search Coordinator <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 20, padding: '2px 8px', marginLeft: 6 }}>By design</span></h3>
          <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>
            The other collaborative tool. Because a team shares one live coverage map, it stores the search boundary,
            pins, and — only while you&rsquo;ve explicitly tapped &ldquo;Start Sharing&rdquo; — your live GPS position
            in the cloud (Firebase), and looks up pin addresses using OpenStreetMap&rsquo;s public geocoder. Access is
            via a private, unguessable link, and it&rsquo;s clearly labeled as cloud-backed inside the app. Together
            with the Video Canvass Tracker, these are the <em>only two</em> tools that use cloud storage.
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>3. Anonymous usage counts</h3>
          <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>
            So we know which tools are useful, the app records <strong>which tool was opened or run</strong> — a tool name and
            a timestamp, nothing more. It never sees, sends, or stores your uploads, inputs, or results. This is standard,
            content-free usage analytics, and many privacy networks block it entirely (which is fine — the tools still work).
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>4. IP and Domain Intelligence <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 20, padding: '2px 8px', marginLeft: 6 }}>Opt-in</span></h3>
          <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: '0 0 10px' }}>
            This tool looks up information <em>about</em> IP addresses, which is only possible using outside data. It is
            deliberately split in two, and the split is visible in the tool:
          </p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, marginBottom: 6 }}>
              <strong style={{ color: INK }}>Screening sends nothing.</strong> The VPN, datacenter, Tor and abuse-blocklist
              checks run against public lists the tool downloads <em>in full</em> and stores in your browser. Downloading
              every known VPN range says nothing about which address you are investigating, so the addresses you paste
              stay on your machine — and screening keeps working with Wi-Fi off.
            </li>
            <li style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, marginBottom: 6 }}>
              <strong style={{ color: INK }}>Live lookup does send the address</strong>, and only when you press the button
              for a specific address. It queries public registration (RDAP at the regional internet registries), DNS
              (dns.google, for the hostname and the announcing network), three geolocation providers (ipwho.is,
              freeipapi.com, ipapi.is), previously observed open ports (Shodan InternetDB), and an abuse-contact
              cross-check (stat.ripe.net). The tool names every recipient before you click. Nothing else you type is
              included, and we neither see nor store any of it.
            </li>
            <li style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65 }}>
              <strong style={{ color: INK }}>Domain and email lookups always use the network</strong>, because no public
              dataset can answer who runs a domain&rsquo;s mail. Pasting a domain queries its registration (RDAP, via
              rdap.org) and its DNS records (dns.google) for mail servers, nameservers and SPF/DMARC. For an email
              address <strong>only the domain is sent</strong> — the mailbox name never leaves your device, and we never
              see either.
            </li>
          </ul>
        </div>

        <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: '0 0 40px' }}>
          That&rsquo;s the complete list. Aside from loading the page itself, downloading public map tiles for the canvass
          and area-search maps, and the public blocklists the IP tool caches for offline screening, there is no other
          outbound traffic.
        </p>

        {/* Verify yourself */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', margin: '0 0 16px' }}>Verify it yourself</h2>
        <ol style={{ paddingLeft: 20, margin: '0 0 44px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, marginBottom: 8 }}>Load the site, then <strong>turn off Wi-Fi</strong> — every tool except Video Canvass and Area Search keeps working. IP and Domain Intelligence still screens addresses offline; only its optional live lookup needs the network.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, marginBottom: 8 }}>In the IP tool specifically: open <strong>Developer Tools → Network</strong>, clear it, and screen a list of addresses. You will see requests for the blocklists themselves and <em>not one</em> containing an address you typed.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.7, marginBottom: 8 }}>Open your browser&rsquo;s <strong>Developer Tools → Network</strong> tab and use a tool. You&rsquo;ll see your file never leaves the page.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.7 }}>The whole app is delivered to your browser as source you can inspect — there is no server-side processing to hide.</li>
        </ol>

        {/* CTA */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '28px', textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>Private by design, verified by review</h2>
          <p style={{ fontSize: 14.5, color: MUTED, margin: '0 0 18px' }}>Use the tools with confidence — your case data stays with you.</p>
          <Link to="/tools" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
            padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700, color: '#fff',
            background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`,
          }}>
            Launch the tools →
          </Link>
        </div>
      </main>
    </div>
  );
}

export default CodeValidation;
